import type { DreMouseEvent } from './types';
import { useState, useCallback, useContext } from 'react';
import { useSetRecoilState } from 'recoil';
import { Contexts } from 'react-vtk-js';
import type { MouseHandlers } from '../types';
import { useMouseToWorldPosition } from './useMouseWorldPosition';
import { useEventAction } from './usePicker';
import { useViewportId, mouseAdaptorsState } from './state';

type Props = {
  onDragStart?: Nullable<MouseHandlers['onDragStart']>;
  onDrag?: Nullable<MouseHandlers['onDrag']>;
  onDragEnd?: Nullable<MouseHandlers['onDragEnd']>;
};

type EventHandlers = {
  onMouseDown: NonNullable<MouseHandlers['onMouseDown']>;
  onMouseUp: NonNullable<MouseHandlers['onMouseUp']>;
};

export const useDragEvents = ({ onDrag, onDragStart, onDragEnd }: Props): EventHandlers => {
  const viewportId = useViewportId();
  const setMouseAdaptorsState = useSetRecoilState(mouseAdaptorsState(viewportId));
  const [primitiveInfo, setPrimitiveInfo] = useState(null);
  const mouseToWorldPosition = useMouseToWorldPosition();
  const view = useContext(Contexts.ViewContext);

  const [dragging, setDragging] = useState(false);

  const getMouseWorldPosition = useCallback(
    async (
      displayPosition?: {
        x: number;
        y: number;
      } | null
    ) => {
      const canvas = view?.getOpenGLRenderWindow()?.get().getCanvas();
      if (displayPosition == null || canvas == null) return null;

      const { left, top } = canvas.getBoundingClientRect();
      const posX = left + window.pageXOffset;
      const posY = top + window.pageYOffset;
      const elX = displayPosition.x - posX;
      const elY = displayPosition.y - posY;

      return mouseToWorldPosition({ elX, elY });
    },
    [mouseToWorldPosition, view]
  );

  useEventAction('mouseMove', async ({ displayPosition }) => {
    if (dragging == null || primitiveInfo == null || displayPosition == null) {
      return;
    }

    // @ts-expect-error [EN-10263] - TS2698 - Spread types may only be created from object types.
    onDrag?.({ ...primitiveInfo, worldPosition: await getMouseWorldPosition(displayPosition) });
  });

  const onMouseDown = useCallback(
    async (info: DreMouseEvent) => {
      setMouseAdaptorsState((state) => ({ ...state, disabled: true }));

      setDragging(true);
      // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'DreMouseEvent' is not assignable to parameter of type 'SetStateAction<null>'.
      setPrimitiveInfo(info);

      onDragStart?.({ ...info, worldPosition: await getMouseWorldPosition(info.displayPosition) });
    },
    [onDragStart, setMouseAdaptorsState, getMouseWorldPosition]
  );

  const onMouseUp = useCallback(
    async (info: DreMouseEvent) => {
      setMouseAdaptorsState((state) => ({ ...state, disabled: false }));
      if (dragging === false) return;

      setDragging(false);
      setPrimitiveInfo(null);
      onDragEnd?.({ ...info, worldPosition: await getMouseWorldPosition(info.displayPosition) });
    },
    [dragging, onDragEnd, setMouseAdaptorsState, getMouseWorldPosition]
  );

  return {
    onMouseDown,
    onMouseUp,
  };
};
